/**
 * ClearStoragePage
 *
 * Utility page at /clear and /reset that shows a confirmation dialog before
 * clearing data. /clear does local-only clear. /reset does full account reset
 * (server + local). After clearing, shows the welcome layout with a "Log in"
 * link for quick access back to /signup during testing.
 */

import { useState } from 'react';
import { clearAllData, fullAccountReset } from '../services/storage/userDataStorage';
import { forgetHeldSession } from '../services/supabase';
import { COLORS, SPACING, TYPOGRAPHY, SHADOWS } from '../styles/UISystem';
import { ButtonRounded } from '../components/UISystem/Buttons/ButtonRounded';
import WelcomePage from './WelcomePage';

const ClearStoragePage = () => {
  const isFullReset = window.location.pathname === '/reset';
  // ?done=true means reset already cleared everything — skip confirmation
  const [cleared, setCleared] = useState(
    () => new URLSearchParams(window.location.search).get('done') === 'true'
  );

  const handleConfirmClear = async () => {
    console.log('ClearStoragePage:', isFullReset ? 'Full account reset...' : 'Clearing storage...');
    try {
      if (isFullReset) {
        const profileId = localStorage.getItem('profile_user_id');
        if (profileId) {
          await fullAccountReset(profileId);
        } else {
          forgetHeldSession();
          localStorage.clear();
          sessionStorage.clear();
        }
      } else {
        await clearAllData();
      }
      console.log('ClearStoragePage: Clear completed');
      setCleared(true);
    } catch (error) {
      console.error('ClearStoragePage: Error:', error);
    }
  };

  const handleCancel = () => {
    window.location.href = '/';
  };

  // After clearing: show welcome layout with Log in link
  if (cleared) {
    return <WelcomePage showLogIn />;
  }

  // Show confirmation dialog
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: COLORS.WHITE,
          borderRadius: '12px',
          padding: SPACING.LG,
          width: '80%',
          maxWidth: '400px',
          boxShadow: SHADOWS.TIER3_UP,
        }}
      >
        <h3
          style={{
            ...TYPOGRAPHY.HEADER_LARGE,
            color: COLORS.ERROR,
            marginTop: 0,
            marginBottom: SPACING.MD,
          }}
        >
          {isFullReset ? 'Reset Account?' : 'Clear All Storage?'}
        </h3>
        <p
          style={{
            ...TYPOGRAPHY.BODY_MEDIUM,
            marginBottom: SPACING.LG,
          }}
        >
          {isFullReset
            ? 'This will permanently delete your account and all data from the server. Your connections, messages, and profile will be gone. This cannot be undone.'
            : 'This will delete all local data including connections, profile information, and settings. This action cannot be undone.'}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: SPACING.MD,
            marginTop: SPACING.LG,
          }}
        >
          <ButtonRounded text="Cancel" onClick={handleCancel} variant="blank" size="auto" />
          <ButtonRounded
            text={isFullReset ? 'Reset Account' : 'Clear Storage'}
            onClick={handleConfirmClear}
            variant="primary"
            size="auto"
          />
        </div>
      </div>
    </div>
  );
};

export default ClearStoragePage;
